import type { FC, ReactNode } from 'react'
import type { BreadcrumbItem } from '@/types'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Breadcrumbs } from '@/components/generals/breadcrumbs'
import { EnquirySection } from '@/components/generals/enquiry-section'
import { absoluteUrl, breadcrumbJsonLd, organizationJsonLd, ORGANIZATION_ID } from '@/lib/structured-data'
import { cities } from '@/constants/cities'
import { services } from '@/constants/services'
import { siteConfig } from '@/constants/site'

const BREADCRUMBS: Array<BreadcrumbItem> = [
    { name: "Home", path: '/' },
    { name: "Contact", path: '/contact' }
]

const ContactPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Contact us for laptop rental and AMC quotes"
                description={`Get a free quote for laptop rental, desktop rental, IT hardware AMC or refurbished laptops in Delhi NCR. Call ${siteConfig.phone.display} or send your requirement.`}
                path='/contact'
                jsonLd={[
                    organizationJsonLd(),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'ContactPage',
                        name: `Contact ${siteConfig.name}`,
                        url: absoluteUrl('/contact'),
                        about: { '@id': ORGANIZATION_ID }
                    },
                    breadcrumbJsonLd(BREADCRUMBS)
                ]}
            />

            <div className='bg-secondary'>
                <div className='shell pt-8'>
                    <Breadcrumbs items={BREADCRUMBS} />
                </div>
            </div>

            <EnquirySection
                headingLevel='h1'
                title="Get a free quote for your team"
                intro={`Tell us what you need for laptop rental, desktop rental, IT hardware AMC or refurbished laptops, and we will call you back with the right configuration and a clear quote. Prefer to talk now? Call ${siteConfig.phone.display}.`}
                className='pt-10 md:pt-14'
            />

            <section aria-labelledby='contact-areas-title' className='py-20 md:py-24'>
                <div className='shell grid gap-10 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-4'>
                        <h2 id='contact-areas-title' className='text-headline font-semibold'>
                            Where we deliver and support
                        </h2>

                        <p className='mt-5 text-lg leading-relaxed text-muted-foreground'>
                            We work with offices across six cities in Delhi NCR. Pick a city and a service to see the areas we cover.
                        </p>
                    </div>

                    <ul className='grid gap-x-10 border-t border-foreground/15 sm:grid-cols-2 lg:col-span-8'>
                        {cities.map((city) => (
                            <li key={city.slug} className='border-b border-foreground/15 py-5'>
                                <p className='flex items-baseline gap-3 font-heading text-xl font-semibold'>
                                    <span aria-hidden='true' className='font-mono text-xs font-normal text-muted-foreground'>{city.code}</span>
                                    {city.name}
                                </p>

                                <ul className='mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm'>
                                    {services.map((service) => (
                                        <li key={service.slug}>
                                            <Link
                                                href={`/${service.slug}/${city.slug}`}
                                                className='inline-block rounded-sm py-0.5 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline'
                                            >
                                                {service.shortName}
                                                <span className='sr-only'> in {city.name}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        </Layout>
    )
}

export default ContactPage
